import type { FacetEditorialMap } from "./types";

/** Contenu éditorial par couleur (ids alignés sur FACET_REGISTRY.color). */
export const COLOR_EDITORIAL: FacetEditorialMap = {
  black: {
    swatch: "#111318",
    related: [
      { key: "shape", valueId: "rectangle" },
      { key: "material", valueId: "plastic" },
      { key: "gender", valueId: "unisex" },
    ],
    fr: {
      suitsTitle: "À qui vont les lunettes noires ?",
      suits:
        "Le noir est la couleur la plus portée, et la plus simple à assortir : il s'accorde avec toutes les carnations et toutes les tenues. Sur une peau foncée, un noir mat structure le regard sans le durcir ; un noir brillant, lui, capte davantage la lumière.",
      keyPoints: [
        { title: "Polyvalence", body: "Une seule paire pour le bureau, les sorties et les cérémonies." },
        { title: "Effet visuel", body: "Le noir marque la ligne des sourcils : il donne du caractère à un visage doux." },
        { title: "Entretien", body: "Les micro-rayures se voient moins sur un noir mat que sur une couleur brillante." },
      ],
      advice:
        "Si vous portez vos lunettes toute la journée, choisissez une monture noire fine plutôt qu'épaisse : elle allège le visage sur les photos et en visioconférence.",
      faq: [
        { q: "Les lunettes noires vont-elles à toutes les peaux ?", a: "Oui. C'est la couleur la plus universelle. Sur les carnations claires, un noir mat évite un contraste trop dur." },
        { q: "Noir mat ou noir brillant ?", a: "Le mat est plus discret et masque mieux l'usure ; le brillant est plus habillé. Les deux sont disponibles selon les modèles en stock." },
      ],
    },
    en: {
      suitsTitle: "Who do black glasses suit?",
      suits:
        "Black is the most worn colour and the easiest to match: it works with every skin tone and every outfit. On darker skin, matte black frames the gaze without hardening it, while gloss black catches more light.",
      keyPoints: [
        { title: "Versatility", body: "One pair for the office, going out and formal occasions." },
        { title: "Visual effect", body: "Black defines the brow line and adds character to softer features." },
        { title: "Care", body: "Micro-scratches show less on matte black than on a glossy colour." },
      ],
      advice:
        "If you wear your glasses all day, choose a thin black frame rather than a thick one: it looks lighter in photos and on video calls.",
      faq: [
        { q: "Do black glasses suit every skin tone?", a: "Yes, it is the most universal colour. On lighter skin, matte black avoids too harsh a contrast." },
        { q: "Matte or gloss black?", a: "Matte is more discreet and hides wear better; gloss is dressier. Both are available depending on models in stock." },
      ],
    },
  },

  brown: {
    swatch: "#6b4326",
    related: [
      { key: "shape", valueId: "cateye" },
      { key: "material", valueId: "plastic" },
      { key: "gender", valueId: "women" },
    ],
    fr: {
      suitsTitle: "À qui vont les lunettes marron ?",
      suits:
        "Le marron et l'écaille sont les alternatives chaudes au noir. Ils réchauffent les carnations dorées et brunes et se fondent naturellement dans le visage, ce qui les rend très faciles à porter au quotidien.",
      keyPoints: [
        { title: "Naturel", body: "Moins tranché que le noir : la monture se remarque sans dominer le visage." },
        { title: "Effet visuel", body: "L'écaille apporte de la nuance et fonctionne particulièrement bien sur les montures épaisses." },
        { title: "Assortiment", body: "S'accorde avec les tenues chaudes, les tissus imprimés et les bijoux dorés." },
      ],
      advice:
        "Sur une monture écaille, chaque paire est légèrement unique : les motifs de l'acétate ne se répètent jamais exactement d'un modèle à l'autre.",
      faq: [
        { q: "Marron ou noir : que choisir ?", a: "Le marron adoucit et réchauffe, le noir structure. Si vous hésitez, essayez les deux en boutique lors de votre test de vue gratuit." },
        { q: "L'écaille compte-t-elle comme du marron ?", a: "Oui, les montures écaille figurent dans cette sélection : leur teinte dominante est brune." },
      ],
    },
    en: {
      suitsTitle: "Who do brown glasses suit?",
      suits:
        "Brown and tortoise are the warm alternatives to black. They warm up golden and deep skin tones and blend naturally into the face, which makes them very easy to wear every day.",
      keyPoints: [
        { title: "Natural", body: "Less sharp than black: the frame is noticed without dominating the face." },
        { title: "Visual effect", body: "Tortoise adds depth and works especially well on thicker frames." },
        { title: "Matching", body: "Pairs with warm outfits, printed fabrics and gold jewellery." },
      ],
      advice:
        "With tortoise frames, every pair is slightly unique: the acetate pattern never repeats exactly from one model to the next.",
      faq: [
        { q: "Brown or black: which should I pick?", a: "Brown softens and warms, black structures. If in doubt, try both in store during your free eye test." },
        { q: "Does tortoise count as brown?", a: "Yes, tortoise frames appear in this selection: their dominant tone is brown." },
      ],
    },
  },

  gold: {
    swatch: "#c9a227",
    related: [
      { key: "material", valueId: "metal" },
      { key: "shape", valueId: "aviator" },
      { key: "gender", valueId: "women" },
    ],
    fr: {
      suitsTitle: "À qui vont les lunettes dorées ?",
      suits:
        "Le doré illumine les carnations foncées et dorées. En métal fin, il reste discret tout en apportant une touche habillée — c'est la couleur la plus demandée sur les formes aviateur et rondes.",
      keyPoints: [
        { title: "Élégance", body: "Une monture dorée fine se remarque sans jamais paraître ostentatoire." },
        { title: "Légèreté", body: "Le doré étant presque toujours en métal, la monture pèse peu sur le nez." },
        { title: "Assortiment", body: "S'accorde naturellement aux bijoux dorés et aux tenues traditionnelles." },
      ],
      advice:
        "Sur une monture dorée, rincez régulièrement les plaquettes à l'eau claire : la transpiration ternit plus vite les finitions métalliques que l'acétate.",
      faq: [
        { q: "Les lunettes dorées vieillissent-elles bien ?", a: "Oui, à condition de les nettoyer régulièrement. Un simple rinçage à l'eau tiède préserve la finition." },
        { q: "Doré ou argenté ?", a: "Le doré réchauffe les carnations foncées et dorées ; l'argenté est plus froid et plus discret." },
      ],
    },
    en: {
      suitsTitle: "Who do gold glasses suit?",
      suits:
        "Gold lights up deep and golden skin tones. In thin metal it stays discreet while adding a dressed-up touch — it is the most requested colour on aviator and round shapes.",
      keyPoints: [
        { title: "Elegance", body: "A thin gold frame gets noticed without ever looking showy." },
        { title: "Lightness", body: "Gold frames are almost always metal, so they sit lightly on the nose." },
        { title: "Matching", body: "Naturally pairs with gold jewellery and traditional outfits." },
      ],
      advice:
        "On gold frames, rinse the nose pads with clear water regularly: perspiration dulls metal finishes faster than acetate.",
      faq: [
        { q: "Do gold glasses age well?", a: "Yes, provided you clean them regularly. A rinse in lukewarm water preserves the finish." },
        { q: "Gold or silver?", a: "Gold warms deep and golden skin tones; silver is cooler and more discreet." },
      ],
    },
  },

  blue: {
    swatch: "#22a6e0",
    related: [
      { key: "shape", valueId: "rectangle" },
      { key: "material", valueId: "plastic" },
      { key: "gender", valueId: "unisex" },
    ],
    fr: {
      suitsTitle: "À qui vont les lunettes bleues ?",
      suits:
        "Le bleu est la couleur d'entrée idéale quand on veut sortir du noir sans prendre de risque. Un bleu marine reste sobre au bureau ; un bleu vif se remarque davantage et fonctionne bien sur les montures fines.",
      keyPoints: [
        { title: "Équilibre", body: "Plus original que le noir, plus discret qu'un rouge ou un orange." },
        { title: "Effet visuel", body: "Le bleu marine adoucit le contour du visage tout en gardant une lecture nette." },
        { title: "Assortiment", body: "Se marie facilement au denim, au blanc et aux tenues de travail." },
      ],
      advice:
        "Si c'est votre première monture colorée, commencez par un bleu marine : il se comporte comme un neutre tout en apportant une nuance.",
      faq: [
        { q: "Les lunettes bleues sont-elles trop voyantes pour le travail ?", a: "Un bleu marine ou pétrole passe partout. Réservez les bleus vifs aux montures fines si votre environnement est formel." },
        { q: "Le bleu va-t-il aux peaux foncées ?", a: "Oui, particulièrement les bleus profonds et le bleu nuit, qui offrent un joli contraste sans dureté." },
      ],
    },
    en: {
      suitsTitle: "Who do blue glasses suit?",
      suits:
        "Blue is the ideal first step away from black without taking a risk. Navy stays understated at the office, while a bright blue stands out more and works well on thin frames.",
      keyPoints: [
        { title: "Balance", body: "More original than black, more discreet than red or orange." },
        { title: "Visual effect", body: "Navy softens the outline of the face while keeping a crisp look." },
        { title: "Matching", body: "Pairs easily with denim, white and workwear." },
      ],
      advice:
        "If it is your first coloured frame, start with navy: it behaves like a neutral while still adding a tone.",
      faq: [
        { q: "Are blue glasses too loud for work?", a: "Navy or petrol blue works anywhere. Keep bright blues for thin frames if your environment is formal." },
        { q: "Does blue suit deeper skin tones?", a: "Yes, especially deep and midnight blues, which give a nice contrast without harshness." },
      ],
    },
  },

  grey: {
    swatch: "#8a8f98",
    related: [
      { key: "material", valueId: "metal" },
      { key: "shape", valueId: "oval" },
      { key: "gender", valueId: "unisex" },
    ],
    fr: {
      suitsTitle: "À qui vont les lunettes grises ?",
      suits:
        "Le gris — y compris le gunmetal et l'argenté — est le neutre le plus discret. Il se fait oublier sur le visage, ce qui en fait un excellent choix pour une paire portée en permanence.",
      keyPoints: [
        { title: "Discrétion", body: "La monture s'efface au profit du regard." },
        { title: "Effet visuel", body: "Moins contrasté que le noir : adapté aux traits déjà marqués." },
        { title: "Entretien", body: "Le gunmetal masque bien les traces de doigts et l'usure du temps." },
      ],
      advice:
        "Le gris fonctionne particulièrement bien en métal fin et en TR90 : deux matières légères pour un port de longue durée.",
      faq: [
        { q: "Le gris fait-il vieillir ?", a: "Non. C'est le contraste de la monture avec la peau, pas la couleur elle-même, qui marque les traits." },
        { q: "Gris ou noir pour une première paire ?", a: "Le gris est plus doux et plus facile à porter tous les jours ; le noir affirme davantage le regard." },
      ],
    },
    en: {
      suitsTitle: "Who do grey glasses suit?",
      suits:
        "Grey — including gunmetal and silver — is the most discreet neutral. It disappears on the face, which makes it an excellent choice for a pair worn all the time.",
      keyPoints: [
        { title: "Discretion", body: "The frame steps back and lets the eyes lead." },
        { title: "Visual effect", body: "Less contrast than black: suited to already defined features." },
        { title: "Care", body: "Gunmetal hides fingerprints and wear well." },
      ],
      advice:
        "Grey works particularly well in thin metal and TR90: two light materials for long wear.",
      faq: [
        { q: "Does grey look ageing?", a: "No. It is the contrast between frame and skin, not the colour itself, that emphasises features." },
        { q: "Grey or black for a first pair?", a: "Grey is softer and easier to wear daily; black states the gaze more strongly." },
      ],
    },
  },

  green: {
    swatch: "#2f7a4f",
    related: [
      { key: "material", valueId: "plastic" },
      { key: "shape", valueId: "square" },
      { key: "gender", valueId: "unisex" },
    ],
    fr: {
      suitsTitle: "À qui vont les lunettes vertes ?",
      suits:
        "Le vert kaki ou bouteille est une alternative chaude et originale au marron. Il flatte les carnations dorées et foncées et donne un rendu naturel, moins strict que le noir.",
      keyPoints: [
        { title: "Originalité", body: "Une couleur remarquée mais jamais criarde en teinte foncée." },
        { title: "Effet visuel", body: "Le vert profond fait ressortir les yeux marron et noisette." },
        { title: "Assortiment", body: "S'accorde aux tenues terreuses, au beige et aux imprimés." },
      ],
      advice:
        "Pour un usage professionnel, préférez un vert foncé (bouteille, kaki) : il se lit presque comme un neutre à distance.",
      faq: [
        { q: "Le vert convient-il à un usage quotidien ?", a: "Oui, en teinte foncée. Les verts clairs conviennent mieux à une seconde paire." },
        { q: "Quelle matière pour une monture verte ?", a: "L'acétate offre la meilleure profondeur de couleur ; le métal vert reste plus rare en stock." },
      ],
    },
    en: {
      suitsTitle: "Who do green glasses suit?",
      suits:
        "Khaki or bottle green is a warm, original alternative to brown. It flatters golden and deep skin tones and gives a natural look, less strict than black.",
      keyPoints: [
        { title: "Originality", body: "A colour that gets noticed but never shouts in a dark shade." },
        { title: "Visual effect", body: "Deep green brings out brown and hazel eyes." },
        { title: "Matching", body: "Pairs with earthy outfits, beige and prints." },
      ],
      advice:
        "For professional use, prefer a dark green (bottle, khaki): from a distance it reads almost like a neutral.",
      faq: [
        { q: "Is green suitable for daily wear?", a: "Yes, in a dark shade. Lighter greens work better as a second pair." },
        { q: "Which material for a green frame?", a: "Acetate gives the best colour depth; green metal frames are rarer in stock." },
      ],
    },
  },

  red: {
    swatch: "#b3252b",
    related: [
      { key: "gender", valueId: "women" },
      { key: "shape", valueId: "cateye" },
      { key: "material", valueId: "plastic" },
    ],
    fr: {
      suitsTitle: "À qui vont les lunettes rouges ?",
      suits:
        "Le rouge est une couleur d'affirmation. Sur un visage neutre, il devient l'accessoire principal de la tenue. Il fonctionne mieux en monture fine à moyenne pour rester équilibré.",
      keyPoints: [
        { title: "Caractère", body: "La monture devient un accessoire de style à part entière." },
        { title: "Effet visuel", body: "Le rouge éclaire le teint et attire le regard vers les yeux." },
        { title: "Usage", body: "Excellent en seconde paire, à alterner avec un neutre." },
      ],
      advice:
        "Essayez toujours une monture rouge en lumière naturelle : le rendu de la couleur change beaucoup entre l'éclairage du magasin et l'extérieur.",
      faq: [
        { q: "Le rouge convient-il au bureau ?", a: "En monture fine et en teinte bordeaux, oui. Les rouges vifs et épais conviennent mieux à un usage personnel." },
        { q: "Le bordeaux compte-t-il comme du rouge ?", a: "Oui, les teintes bordeaux et bordeaux-brun figurent dans cette sélection." },
      ],
    },
    en: {
      suitsTitle: "Who do red glasses suit?",
      suits:
        "Red is a statement colour. On a neutral face it becomes the main accessory of the outfit. It works best in thin to medium frames to stay balanced.",
      keyPoints: [
        { title: "Character", body: "The frame becomes a style accessory in its own right." },
        { title: "Visual effect", body: "Red brightens the complexion and draws attention to the eyes." },
        { title: "Use", body: "Excellent as a second pair, alternating with a neutral." },
      ],
      advice:
        "Always try a red frame in natural light: the colour reads very differently in store lighting and outdoors.",
      faq: [
        { q: "Is red suitable for the office?", a: "In a thin frame and a burgundy shade, yes. Bright, thick reds suit personal use better." },
        { q: "Does burgundy count as red?", a: "Yes, burgundy and burgundy-brown shades are included in this selection." },
      ],
    },
  },

  pink: {
    swatch: "#d98099",
    related: [
      { key: "gender", valueId: "women" },
      { key: "shape", valueId: "round" },
      { key: "material", valueId: "plastic" },
    ],
    fr: {
      suitsTitle: "À qui vont les lunettes roses ?",
      suits:
        "Du rose poudré au rose translucide, cette famille adoucit le regard et éclaire le teint. C'est l'une des couleurs les plus demandées sur les montures féminines rondes et œil-de-chat.",
      keyPoints: [
        { title: "Douceur", body: "Le rose poudré reste discret et se porte tous les jours." },
        { title: "Effet visuel", body: "Le translucide allège visuellement une monture épaisse." },
        { title: "Assortiment", body: "S'accorde aux tenues claires et aux bijoux dorés comme argentés." },
      ],
      advice:
        "Les acétates translucides marquent un peu plus les traces : un nettoyage hebdomadaire à l'eau savonneuse suffit à conserver leur transparence.",
      faq: [
        { q: "Le rose convient-il aux peaux foncées ?", a: "Oui, particulièrement les roses soutenus et les roses translucides, qui créent un contraste lumineux." },
        { q: "Le rose se porte-t-il au travail ?", a: "En teinte poudrée ou translucide, oui : le rendu reste sobre." },
      ],
    },
    en: {
      suitsTitle: "Who do pink glasses suit?",
      suits:
        "From dusty pink to translucent pink, this family softens the gaze and brightens the complexion. It is one of the most requested colours on round and cat-eye women's frames.",
      keyPoints: [
        { title: "Softness", body: "Dusty pink stays discreet and can be worn every day." },
        { title: "Visual effect", body: "Translucent finishes visually lighten a thick frame." },
        { title: "Matching", body: "Pairs with light outfits and both gold and silver jewellery." },
      ],
      advice:
        "Translucent acetates show marks a little more: a weekly clean with soapy water keeps them clear.",
      faq: [
        { q: "Does pink suit deeper skin tones?", a: "Yes, especially saturated and translucent pinks, which create a luminous contrast." },
        { q: "Can pink be worn at work?", a: "In dusty or translucent shades, yes: the result stays understated." },
      ],
    },
  },

  purple: {
    swatch: "#7a5ba6",
    related: [
      { key: "gender", valueId: "women" },
      { key: "shape", valueId: "cateye" },
      { key: "material", valueId: "plastic" },
    ],
    fr: {
      suitsTitle: "À qui vont les lunettes violettes ?",
      suits:
        "Le violet et le lavande sont des couleurs de nuance : assez originales pour se remarquer, assez froides pour rester élégantes. Elles mettent en valeur les yeux marron et noirs.",
      keyPoints: [
        { title: "Originalité", body: "Une alternative au bleu quand on cherche une teinte plus chaude." },
        { title: "Effet visuel", body: "Les violets profonds éclairent le regard sans durcir les traits." },
        { title: "Usage", body: "Parfait en paire principale si la monture est fine." },
      ],
      advice:
        "Le violet passe mieux en acétate qu'en métal : la matière donne de la profondeur à la teinte plutôt qu'un effet laqué.",
      faq: [
        { q: "Le violet se porte-t-il tous les jours ?", a: "Oui en teinte profonde (prune, aubergine). Les lavandes claires conviennent mieux à une seconde paire." },
        { q: "Le violet est-il une couleur unisexe ?", a: "Il est majoritairement choisi sur les montures femme, mais rien n'empêche de le porter sur une forme unisexe." },
      ],
    },
    en: {
      suitsTitle: "Who do purple glasses suit?",
      suits:
        "Purple and lavender are nuanced colours: original enough to be noticed, cool enough to stay elegant. They highlight brown and dark eyes.",
      keyPoints: [
        { title: "Originality", body: "An alternative to blue when you want a warmer tone." },
        { title: "Visual effect", body: "Deep purples brighten the gaze without hardening features." },
        { title: "Use", body: "Perfect as a main pair if the frame is thin." },
      ],
      advice:
        "Purple works better in acetate than metal: the material gives depth rather than a lacquered look.",
      faq: [
        { q: "Can purple be worn daily?", a: "Yes in deep shades (plum, aubergine). Light lavender suits a second pair better." },
        { q: "Is purple a unisex colour?", a: "It is mostly chosen on women's frames, but nothing stops you from wearing it on a unisex shape." },
      ],
    },
  },

  orange: {
    swatch: "#d97a2b",
    related: [
      { key: "material", valueId: "plastic" },
      { key: "shape", valueId: "round" },
      { key: "gender", valueId: "unisex" },
    ],
    fr: {
      suitsTitle: "À qui vont les lunettes oranges ?",
      suits:
        "L'orange, du miel au terracotta, réchauffe immédiatement le teint. Il fonctionne très bien sur les carnations dorées et foncées et donne une lecture solaire au visage.",
      keyPoints: [
        { title: "Chaleur", body: "Une teinte lumineuse, proche de l'écaille mais plus affirmée." },
        { title: "Effet visuel", body: "Le miel translucide allège une monture épaisse." },
        { title: "Usage", body: "Idéal en seconde paire ou pour un style assumé au quotidien." },
      ],
      advice:
        "L'orange translucide vieillit très bien : la couleur ne s'écaille pas puisqu'elle est dans la masse de l'acétate.",
      faq: [
        { q: "L'orange est-il trop voyant ?", a: "En teinte miel ou terracotta, non : c'est proche d'une écaille claire. Les oranges vifs sont plus marqués." },
        { q: "L'orange va-t-il aux peaux foncées ?", a: "Oui, c'est même l'une des combinaisons les plus lumineuses." },
      ],
    },
    en: {
      suitsTitle: "Who do orange glasses suit?",
      suits:
        "Orange, from honey to terracotta, instantly warms the complexion. It works very well on golden and deep skin tones and gives the face a sunny read.",
      keyPoints: [
        { title: "Warmth", body: "A luminous tone, close to tortoise but bolder." },
        { title: "Visual effect", body: "Translucent honey lightens a thick frame." },
        { title: "Use", body: "Ideal as a second pair or for a confident everyday style." },
      ],
      advice:
        "Translucent orange ages very well: the colour cannot chip because it runs through the acetate.",
      faq: [
        { q: "Is orange too loud?", a: "In honey or terracotta, no: it reads close to light tortoise. Bright oranges are more of a statement." },
        { q: "Does orange suit deeper skin tones?", a: "Yes, it is one of the most luminous combinations." },
      ],
    },
  },
};
